import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import PlusLogo from "../../assets/Logo.svg?react";
import LotusLogo from "../../assets/LotusLogo.png";
import TileBackground from "@/customComponents/TileBackground";
import PasswordInputEye from "./PageComponents/PasswordInputEye";
import BgWall from "../../assets/WallBg.png";
import { useEffect, useState } from "react";
import { login } from "@/apiRoutes/login";
import { X } from "lucide-react";
import { toast } from "sonner";
import { WpGlobal } from "@/globals/types";
import { ROLES } from "@/globals/constants";
import { authenticate } from "@/apiRoutes/auth";

const currentUrl = `${window.location.protocol}//${window.location.host}`;
const vendordDashboardUrl = `${currentUrl}/vendordashboard`;
const clientDashboardUrl = `${currentUrl}/clientdashboard`;
const wpAdminUrl = `${currentUrl}/wp-admin`;

declare const wpData: WpGlobal;

export default function Login() {
  const [usernameInput, setUsernameInput] = useState("");
  const [passwordInput, setPasswordInput] = useState("");
  const [userRoles, setUserRoles] = useState([]);
  const [showError, setShowError] = useState(false);

  const handleCloseError = () => setShowError(false);

  useEffect(() => {
    const roles = wpData.user_roles;
    if (roles.length === 0) {
      return;
    }

    handleRedirect(0, roles);
  }, []);

  useEffect(() => {
    if (userRoles.length === 0) {
      return;
    }

    handleRedirect(2000, userRoles);
  }, [userRoles]);

  const delay = (ms: number) =>
    new Promise((resolve) => setTimeout(resolve, ms));

  const handleRedirect = async (msDelay: number, roles: string[]) => {
    await delay(msDelay);
    // const redirectUrl = await authenticate();

    // if (roles.length >= 0) {
    //   window.location.href = redirectUrl;
    // }

    if (roles.includes(ROLES.admin)) {
      window.location.href = wpAdminUrl;
    }

    if (roles.includes(ROLES.vendor)) {
      window.location.href = vendordDashboardUrl;
    }

    if (roles.includes(ROLES.client)) {
      window.location.href = clientDashboardUrl;
    }
  };

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVal = e.target.value;
    setUsernameInput(newVal);
  };

  const loginProcess = async () => {
    const response = await login(usernameInput, passwordInput);

    setUserRoles(response);

    if (response.length === 0) {
      setShowError(true);
      setPasswordInput("");
      throw new Error();
    }
  };

  const handleLogin = () => {
    if (usernameInput === "") {
      toast.error("Please enter username");
      return;
    }

    if (passwordInput === "") {
      toast.error("Please enter password");
      return;
    }

    toast.promise(loginProcess, {
      loading: "Logging in...",
      success: "Login success, redirecting...",
    });
  };

  return (
    <div className="bg-login-gradient-end over relative h-screen p-3">
      <TileBackground />
      <div className="relative z-10 flex h-full w-full flex-col rounded-[10px] border-3 border-[#49A5E426] bg-linear-to-br from-[rgba(73,165,228,0.4)] to-[rgba(207,207,207,0.3)] p-5 shadow-[0_2px_12px_rgba(0,0,0,0.5)] backdrop-blur-xs">
        <div>
          <div className="flex h-72 flex-col items-center justify-center align-middle">
            <PlusLogo className="size-48"></PlusLogo>
          </div>
          <div>
            {showError && (
              <div className="flex justify-center">
                <div className="mb-4 flex w-6/7 max-w-80 items-center justify-between rounded-sm border-1 border-[#f85149] bg-[#ff383847] p-4">
                  <p className="font-gotham mb-0.5 text-sm font-normal text-white">
                    Incorrect username or password
                  </p>
                  <X
                    className="size-6 cursor-pointer text-[#f85149] hover:text-[#dc433b]"
                    onClick={handleCloseError}
                  ></X>
                </div>
              </div>
            )}
            <div className="flex flex-col items-center gap-2">
              <Input
                className="font-gotham w-6/7 max-w-80 rounded-sm border-2 bg-white/10 text-xs font-normal text-white focus:ring-0"
                placeholder="Username/Email"
                value={usernameInput}
                onChange={handleUsernameChange}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleLogin();
                  }
                }}
              />
              <PasswordInputEye
                input={passwordInput}
                onChange={setPasswordInput}
                onEnterPressed={handleLogin}
              />
            </div>
            <div className="mt-10 flex flex-col items-center justify-center gap-4">
              <Button
                className="font-gotham from-button-gradient-start to-button-gradient-end w-1/2 max-w-60 cursor-pointer rounded-4xl border-0 bg-linear-to-r text-xs font-[450] text-white"
                variant="outline"
                onClick={handleLogin}
              >
                Login
              </Button>
              <div className="flex justify-center gap-1">
                <p className="font-gotham text-xs font-[450] text-[#B4B4B4]">
                  Don't have an account?
                </p>
                <p className="font-gotham text-xs font-[450] text-[#49A5E4]">
                  Register
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="flex grow-1 items-end justify-center pb-10">
          <div>
            <img src={LotusLogo} alt="Lotus Logo" className="size-24" />
          </div>
        </div>
      </div>
    </div>
  );
}
